import './AssetDetails.scss';

import { IconCopy, IconShare } from '@hulu-react-style-components/icons';
import React from 'react';

import { AssetDownloadButton } from '../_ForCommonLibrary/components/atoms/AssetDownloadButton';
import type { Asset2ReviewStatus, Asset2Tag, Asset2TranscodingStatus, Maybe } from '../../apis/graphql';
import { stringifyApprovedPublishers } from '../../configs';
import type { Nullable } from '../../models/utils';
import bem from '../../utils/bem';
import { formatBytes, formatDate, formatDuration, stringifyTagList } from '../../utils/formatting';
import Alert, { AlertType } from '../Alert';
import { getColor } from '../AssetPreview/util/getColor';
import Pill from '../Pill';
import { getTranscodeColor } from '../Pill/constants';
import Tooltip from '../Tooltip';

export interface AssetDetailsProps {
  assetDetailsUrl: string;
  assetTagList?: Asset2Tag[];
  approvedPublishers?: Maybe<string[]>;
  createdAt: Date;
  fileSize: Nullable<number>;
  filename?: string;
  fileUrl: string;
  review: string;
  transcodeStatus?: Maybe<string>;
  transcodingDetails: { name: string; message: string };
  isTextAsset: boolean;
  isDisplayAsset: boolean;
  videoDuration?: number;
  assetId?: string;
  creativeId?: string;
  derivedCreativeReviewStatus?: Asset2ReviewStatus;
  derivedCreativeTranscodingStatus?: Maybe<Asset2TranscodingStatus>;
}

// Always link to prod snapshot UI
// TODO: Use the correct Snapshot URL when available
export const BASE_SNAPSHOT_URL = ''; //'http://snapshot-ui.prod.hulu.com/?source=path&validate=1&path=';
const separatorString = ' • ';

export const makeMetadataString = (createdAt: Date, fileSize: Nullable<number>, videoDuration?: number): string => {
  const components: string[] = [];

  if (videoDuration) {
    components.push(formatDuration(videoDuration));
  }
  if (fileSize) {
    components.push(formatBytes(fileSize));
  }

  components.push(`Uploaded ${formatDate(createdAt, false)}`);

  return components.join(separatorString);
};

const [block, element] = bem('asset-details');

function AssetDetails({
  assetDetailsUrl,
  assetTagList,
  createdAt,
  filename,
  fileSize,
  fileUrl,
  review,
  transcodeStatus,
  transcodingDetails,
  isTextAsset,
  isDisplayAsset,
  videoDuration,
  approvedPublishers,
  assetId,
  creativeId,
  derivedCreativeReviewStatus,
  derivedCreativeTranscodingStatus,
}: Readonly<AssetDetailsProps>): JSX.Element {
  const isPending = review === 'PENDING';

  const handleCopyAssetId = (): void => {
    if (assetId) {
      navigator.clipboard.writeText(assetId);
    }
  };

  const isTranscodingErrorAlertShown =
    (transcodingDetails.message || transcodingDetails.name) && !isDisplayAsset && !isTextAsset;
  const isAssetDetailsLinkShown = assetDetailsUrl && !isTextAsset;

  return (
    <div className={block()}>
      {!isTextAsset && (
        <div className={element('header')} data-testid="asset-details-header">
          <Tooltip
            id={`asset-name-tooltip`}
            message={'Original asset name. May differ from file name if same asset is uploaded multiple times.'}
          >
            <p className={element('name')}>{filename}</p>
          </Tooltip>
          <div className={element('download')}>
            <AssetDownloadButton filename={filename} url={fileUrl} />
          </div>
        </div>
      )}
      {review && !isTextAsset && (
        <div className={element('status-row')}>
          Asset Review Status:
          <Pill className={element('status-pill')} text={review} color={getColor(review)} />
        </div>
      )}
      {transcodeStatus && !isTextAsset && (
        <div className={`${element('status-row')} ${element('status-row', 'spaced')}`}>
          Asset Transcoding Status:
          <Pill className={element('status-pill')} text={transcodeStatus} color={getTranscodeColor(transcodeStatus)} />
        </div>
      )}
      {isTranscodingErrorAlertShown && (
        <div className={element('transcoding-details')}>
          <Alert
            headerText={isPending ? '' : `Transcoding Error: ${transcodingDetails.name || ''}`}
            type={isPending ? AlertType.WARNING : AlertType.ERROR}
            bodyText={
              isPending ? 'ASSET PENDING PRE-QC INGESTION. Check back later.' : transcodingDetails.message || ''
            }
          />
        </div>
      )}
      <p className={element('metadata')}>
        {makeMetadataString(createdAt, fileSize, videoDuration)}
        {isAssetDetailsLinkShown && (
          <>
            {separatorString}
            <a className={element('review-task-link')} href={assetDetailsUrl} target="_blank" rel="noopener noreferrer">
              <span>Asset Details</span> <IconShare title="Asset Details external link" />
            </a>
          </>
        )}
      </p>
      <div className={element('asset-tags')}>
        <p className={`${element('label')} ${element('asset-tags-part')}`}>{'Asset ID:'}</p>
        <p className={`${element('tags')} ${element('asset-tags-part')}`}>
          {assetId}
          {assetId && (
            <button
              type="button"
              onClick={handleCopyAssetId}
              className={element('copy-button')}
              aria-label="Copy Asset ID"
              title="Copy Asset ID"
            >
              <IconCopy />
            </button>
          )}
        </p>
      </div>
      <div className={element('asset-tags')}>
        <p className={`${element('label')} ${element('asset-tags-part')}`}>{'Asset tags:'}</p>
        <p className={`${element('tags')} ${element('asset-tags-part')}`}>{stringifyTagList(assetTagList ?? [])}</p>
      </div>
      <div className={element('asset-tags')}>
        <p className={`${element('label')} ${element('asset-tags-part')}`}>{'Approved Publishers:'}</p>
        <p className={`${element('tags')} ${element('asset-tags-part')}`}>
          {stringifyApprovedPublishers(approvedPublishers)}
        </p>
      </div>
      <div className={element('asset-tags')}>
        <p className={`${element('label')} ${element('asset-tags-part')}`}>{'Creative ID:'}</p>
        <p className={`${element('tags')} ${element('asset-tags-part')}`}>{creativeId}</p>
      </div>
      {derivedCreativeReviewStatus && (
        <div className={element('asset-tags')}>
          <p className={`${element('label')} ${element('asset-tags-part')}`}>{'Derived Creative Review Status:'}</p>
          <p className={`${element('tags')} ${element('asset-tags-part')}`}>
            <Pill
              className={element('status-pill')}
              text={derivedCreativeReviewStatus}
              color={getColor(derivedCreativeReviewStatus)}
            />
          </p>
        </div>
      )}
      {derivedCreativeTranscodingStatus && (
        <div className={`${element('asset-tags')} ${element('asset-tags', 'spaced')}`}>
          <p className={`${element('label')} ${element('asset-tags-part')}`}>
            {'Derived Creative Transcoding Status:'}
          </p>
          <p className={`${element('tags')} ${element('asset-tags-part')}`}>
            <Pill
              className={element('status-pill')}
              text={derivedCreativeTranscodingStatus}
              color={getTranscodeColor(derivedCreativeTranscodingStatus)}
            />
          </p>
        </div>
      )}
    </div>
  );
}

export default AssetDetails;
